import React, { useState, useCallback, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { sortByDeploymentPercent } from '../common/utils';
import { DashboardPoliciesTableStyles } from '../Styles';
import { 
  PolicyTableContainer,
  PolicyTable,
  EntityFilterBar, 
  DashboardViewDetailsContent,
  TenantExpandedRow,
  DashboardPolicyRow,
  DASHBOARD_GRID_COLUMNS,
  DASHBOARD_GRID_COLUMNS_NO_PRIORITY,
  PoliciesPaginationFooter,
} from './shared';
import { TenantSelectionStep, RuleSelectionStep, SummaryStep, DeploySummary } from './deploymentWizard';
import { useToggleSet } from '../../hooks/useToggleSet';
import { useDialogState } from '../../hooks/useDialogState';
import { useDeploymentWizard } from './useDeploymentWizard';
import { usePolicyTableState } from './usePolicyTableState';
import { useServerPagination } from './useServerPagination';
import { useSummaryRefresh } from './useSummaryRefresh';
import { DIALOG_STYLES, STATUS_OPTIONS } from './constants';
import { ensureDomainSuffix, isGuid, mapTabToPolicyType } from './utils';
import type { SummaryEntity } from './utils';
import { useAppDispatch, useAppSelector } from '../../hooks';
import DeploymentWarningBanner from '../common/DeploymentWarningBanner';
import {
  fetchDashboardPolicies,
  selectDashboardPolicies,
  selectDashboardPoliciesLoading,
  selectDashboardPoliciesError,
  selectDashboardPoliciesMetadata,
  updateTenantDeploymentStatuses,
} from '../../slices/dashboardPoliciesSlice';
import { buildDashboardDeployRequest, batchDeploy } from '../../services/deployment.service';
import {
  DashboardPolicy,
  entityMatchKey,
  labelMatchKey,
  labelDisplayText,
  DeploymentResult,
  TrackedDeployment,
} from './types';
import { trackDeployments, selectActiveDeploymentCount } from '../../slices/deploymentTrackingSlice';
import { selectTenantEntities } from '../../slices/tenantsSlice';

export default function DashboardPoliciesTable() {
  const styles = DashboardPoliciesTableStyles();
  const dispatch = useAppDispatch();
  
  // Redux state
  const allPolicies = useAppSelector(selectDashboardPolicies);
  const loading = useAppSelector(selectDashboardPoliciesLoading);
  const error = useAppSelector(selectDashboardPoliciesError);
  const metadata = useAppSelector(selectDashboardPoliciesMetadata);
  
  // Global tenant entities for name/domain fallback
  const tenantEntities = useAppSelector(selectTenantEntities);

  // Active deployment count (polling runs at App level)
  const activeDeployCount = useAppSelector(selectActiveDeploymentCount);
  
  // Use shared policy table state hook — per-type tab counts come from the server
  // (independent of which tab/policyType is currently loaded).
  const {
    selectedTab,
    setSelectedTab,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    filteredPolicies,
    dlpCount,
    autoLabelCount,
    resetFilters,
    deployMessage,
    setDeployMessage,
  } = usePolicyTableState({
    policies: allPolicies,
    dlpTotalCount: metadata.dlpTotalCount,
    autoLabelTotalCount: metadata.autoLabelTotalCount,
  });

  // Local page state + debounced search (snap back to page 1 when search changes).
  const { pageNumber, setPageNumber, debouncedSearch } = useServerPagination(search);
  
  // Expand state for policy rows
  const [expanded, , setExpanded] = useToggleSet();
  
  // Dialog states with useDialogState hook
  const viewDetailsDialog = useDialogState<DashboardPolicy>();
  
  // Deployment wizard state (useReducer-based)
  const { state: deployWizard, actions: deployWizardActions } = useDeploymentWizard();
  
  // Local state for tenant selection during tenant-selection step (before moving to rule-selection)
  const [localSelectedTenants, setLocalSelectedTenants] = useState<Set<string>>(new Set());

  // Whether the summary step is re-fetching fresh deployment status. Derived synchronously on
  // entering the step (see useSummaryRefresh) so the first summary render is never "settled".
  const summaryRefresh = useSummaryRefresh(
    deployWizard.step === 'summary',
    useCallback(
      () =>
        dispatch(
          fetchDashboardPolicies({
            policyType: mapTabToPolicyType(selectedTab),
            pageNumber,
            searchTerm: debouncedSearch || undefined,
          }),
        ).unwrap(),
      [dispatch, selectedTab, pageNumber, debouncedSearch],
    ),
  );
  
  // Reset local tenant selection when wizard closes or starts fresh
  useEffect(() => {
    if (deployWizard.step === 'closed') {
      setLocalSelectedTenants(new Set());
    }
  }, [deployWizard.step]);
  
  // Track if we've initialized the active tenant for current rule-selection session
  const [hasInitializedActiveTenant, setHasInitializedActiveTenant] = useState(false);
  
  // Reset initialization flag when wizard closes or goes back to tenant selection
  useEffect(() => {
    if (deployWizard.step === 'closed' || deployWizard.step === 'tenant-selection') {
      setHasInitializedActiveTenant(false);
    }
  }, [deployWizard.step]);
  
  // Set active tenant to first in sorted order when entering rule-selection step (only once)
  useEffect(() => {
    if (deployWizard.step === 'rule-selection' && deployWizard.policy && !hasInitializedActiveTenant) {
      const tenantIds = Array.from(deployWizard.selectedTenantIds);
      const corpRules = deployWizard.policy.rules;
      const totalRules = corpRules.length;
      const corpRuleIds = new Set(corpRules.map(r => r.ruleId));
      
      // Build deployment items for sorting
      const deploymentItems = tenantIds.map(tenantId => {
        const tenantDeployment = deployWizard.policy?.tenantDeployments.find(
          td => td.tenantId.toLowerCase() === tenantId.toLowerCase()
        );
        const deployedCount = tenantDeployment?.ruleStatuses.filter(
          rs => rs.isDeployed && corpRuleIds.has(rs.ruleId)
        ).length || 0;
        return { id: tenantId, deployedCount, totalCount: totalRules };
      });
      
      // Sort tenants: not deployed (red) → partial (amber) → fully deployed (green)
      const { firstId } = sortByDeploymentPercent(deploymentItems);
      
      // Set active tenant to first in sorted order
      if (firstId) {
        deployWizardActions.setActiveTenant(firstId);
        setHasInitializedActiveTenant(true);
      }
    }
  }, [deployWizard.step, deployWizard.policy, deployWizard.selectedTenantIds, hasInitializedActiveTenant, deployWizardActions]);
  
  // Fetch policies whenever the active tab, page, or debounced search changes.
  useEffect(() => {
    dispatch(
      fetchDashboardPolicies({
        policyType: mapTabToPolicyType(selectedTab),
        pageNumber,
        searchTerm: debouncedSearch || undefined,
      }),
    );
  }, [dispatch, selectedTab, pageNumber, debouncedSearch]);

  // Re-fetching on entry to the summary step is handled by useSummaryRefresh above: it derives
  // the refreshing state during render (no stale first frame) and surfaces failures instead of
  // swallowing them, so Confirm stays disabled until the status really was revalidated.

  // Handle expand toggle for policy rows
  const handleToggleExpand = (policyId: string) => {
    setExpanded((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(policyId)) {
        newSet.delete(policyId);
      } else {
        newSet.add(policyId);
      }
      return newSet;
    });
  };

  // Handle View Details
  const handleViewDetails = (policy: DashboardPolicy) => {
    viewDetailsDialog.open(policy);
  };

  // Handle starting deployment wizard - resets local state first
  const handleStartDeployment = useCallback((policy: DashboardPolicy) => {
    setLocalSelectedTenants(new Set()); // Reset local tenant selection
    deployWizardActions.startDeployment(policy);
  }, [deployWizardActions]);

  // Handle tenant selection - managed locally during tenant-selection step
  const handleTenantToggle = useCallback((tenantId: string, checked: boolean) => {
    setLocalSelectedTenants(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(tenantId);
      } else {
        newSet.delete(tenantId);
      }
      return newSet;
    });
  }, []);

  // Handle Next from tenant selection to rule selection
  const handleNextToRuleSelection = () => {
    if (!deployWizard.policy) return;
    
    if (localSelectedTenants.size === 0) {
      return;
    }
    
    deployWizardActions.goToRuleSelection(localSelectedTenants);
  };

  // Helper to get tenant name/domain by ID (case-insensitive, handles empty names)
  const getTenantNameById = useCallback((tenantId: string): string => {
    // 1. Try the policy-level tenant deployment data (skip if it's a GUID)
    const deployment = deployWizard.policy?.tenantDeployments.find(
      td => td.tenantId.toLowerCase() === tenantId.toLowerCase()
    );
    const deploymentName = deployment?.tenantName?.trim();
    if (deploymentName && !isGuid(deploymentName) && deploymentName !== tenantId) {
      return ensureDomainSuffix(deploymentName);
    }

    // 2. Try the global tenants store (authorized tenants API)
    const globalTenant = tenantEntities[tenantId];
    if (globalTenant?.name?.trim()) return ensureDomainSuffix(globalTenant.name.trim());

    // 3. Fall back to raw tenant ID
    return tenantId;
  }, [deployWizard.policy, tenantEntities]);

  // Helper to get tenant label prefix by ID ('' when none configured)
  const getTenantPrefixById = useCallback((tenantId: string): string => {
    const exact = tenantEntities[tenantId];
    if (exact) return exact.prefix ?? '';
    // Fall back to case-insensitive match: entity keys may differ in case from the deployment tenantId.
    const lowerId = tenantId.toLowerCase();
    const match = Object.values(tenantEntities).find((t) => t?.id?.toLowerCase() === lowerId);
    return match?.prefix ?? '';
  }, [tenantEntities]);

  // Handle rule selection for active tenant
  const handleRuleToggle = (ruleId: string, checked: boolean) => {
    deployWizardActions.toggleRule(deployWizard.activeTenantId, ruleId);
  };

  // Get selected rules for active tenant
  const getActiveRuleIds = () => {
    return deployWizard.tenantRuleSelections.get(deployWizard.activeTenantId) || new Set<string>();
  };

  // Deploy using BFF deployment API
  const handleConfirmDeploy = async () => {
    if (!deployWizard.policy) return;
    
    try {
      // Build BFF deployment request from wizard state
      const request = buildDashboardDeployRequest(
        deployWizard.policy,
        deployWizard.tenantRuleSelections,
      );

      if (request.entities.length === 0 || request.entities[0].targets.length === 0) {
        setDeployMessage({ intent: 'warning', title: 'No Items', body: 'No deployment items to submit.' });
        return;
      }

      // Call the BFF deploy API
      const result: DeploymentResult = await batchDeploy(request);

      // Update local Redux state with new statuses
      if (result.statusUpdates.size > 0) {
        dispatch(updateTenantDeploymentStatuses(Array.from(result.statusUpdates.entries())));
      }

      // Register accepted items for deployment status polling
      if (result.acceptedItems.length > 0) {
        const now = Date.now();
        const trackedItems: TrackedDeployment[] = result.acceptedItems.map(item => ({
          deploymentId: item.deploymentId,
          tenantId: item.tenantId,
          entityId: item.entityId,
          policyName: deployWizard.policy?.policyName || item.entityId,
          view: 'dashboard' as const,
          apiStatus: 'Received',
          startedAt: now,
          lastPolledAt: now,
        }));
        dispatch(trackDeployments(trackedItems));
      }

      // Show message bar
      if (result.success && result.failedCount === 0) {
        setDeployMessage({ intent: 'success', title: 'Deployment Initiated', body: result.message });
      } else if (result.success) {
        setDeployMessage({ intent: 'warning', title: 'Partial Deployment', body: result.message });
      } else {
        setDeployMessage({ intent: 'error', title: 'Deployment Failed', body: result.message });
      }

      // Close wizard and refresh data
      deployWizardActions.reset();
      dispatch(
        fetchDashboardPolicies({
          policyType: mapTabToPolicyType(selectedTab),
          pageNumber,
          searchTerm: debouncedSearch || undefined,
        }),
      );
    } catch (err) {
      console.error('Deployment failed:', err);
      setDeployMessage({
        intent: 'error',
        title: 'Deployment Error',
        body: err instanceof Error ? err.message : 'An unexpected error occurred.',
      });
    }
  };

  // Handle Next from rule selection to summary
  const handleNextToSummary = () => {
    deployWizardActions.goToSummary();
  };

  // Handle Back from summary to rule selection
  const handleBackToRuleSelection = () => {
    deployWizardActions.goBackToRuleSelection();
  };

  // Get summary data for deploy
  const getDeploySummary = (): DeploySummary | null => {
    if (!deployWizard.policy) return null;
    
    const tenantIds = Array.from(deployWizard.selectedTenantIds);
    const corpRules = deployWizard.policy.rules;
    
    // Collect all unique SITs and Labels from selected rules.
    // Keyed by match key (lower-cased internal name) so existence comparison lines up
    // with the backend, but carrying the display text so the summary stays readable.
    const allSits = new Map<string, string>();
    const allLabels = new Map<string, string>();
    
    tenantIds.forEach(tenantId => {
      const selectedRuleIds = deployWizard.tenantRuleSelections.get(tenantId) || new Set();
      
      // Add SITs/Labels from selected rules (use ruleId for DashboardPolicyRule)
      corpRules.filter(r => selectedRuleIds.has(r.ruleId)).forEach(rule => {
        (rule.sits || []).forEach(sit => {
          const key = entityMatchKey(sit.name);
          if (key) allSits.set(key, sit.name!);
        });
        (rule.labels || []).forEach(label => {
          const key = labelMatchKey(label);
          if (key) allLabels.set(key, labelDisplayText(label));
        });
      });
    });
    
    // Build per-tenant deployed SIT/Label sets using sitStatuses/labelStatuses.
    // Source the per-tenant deployment status from the LIVE Redux slice (refreshed
    // on entering the summary step) rather than the wizard's frozen policy snapshot,
    // so labels deployed since the wizard opened are correctly treated as existing.
    const freshPolicy = allPolicies.find(
      p => p.policyId === deployWizard.policy!.policyId
    );
    const statusSourcePolicy = freshPolicy ?? deployWizard.policy!;

    const deployedSitsPerTenant = new Map<string, Set<string>>();
    const deployedLabelsPerTenant = new Map<string, Set<string>>();
    
    tenantIds.forEach(tenantId => {
      const deployment = statusSourcePolicy.tenantDeployments.find(
        td => td.tenantId.toLowerCase() === tenantId.toLowerCase()
      );
      
      const tenantSits = new Set<string>();
      const tenantLabels = new Set<string>();
      
      deployment?.sitStatuses
        ?.filter(s => s.isDeployed)
        .forEach(s => tenantSits.add(entityMatchKey(s.entityName)));
      
      deployment?.labelStatuses
        ?.filter(l => l.isDeployed)
        .forEach(l => tenantLabels.add(entityMatchKey(l.entityName)));
      
      deployedSitsPerTenant.set(tenantId, tenantSits);
      deployedLabelsPerTenant.set(tenantId, tenantLabels);
    });
    
    // A SIT/Label is "existing" if deployed in ALL selected tenants;
    // otherwise it will need to be created in at least one tenant.
    // Carry both the match key (used for comparison and as the React key) and the display text.
    const sitsExisting: SummaryEntity[] = [];
    const sitsNew: SummaryEntity[] = [];
    allSits.forEach((display, key) => {
      const inAll = tenantIds.every(tid => deployedSitsPerTenant.get(tid)?.has(key) ?? false);
      (inAll ? sitsExisting : sitsNew).push({ key, display });
    });

    const labelsExisting: SummaryEntity[] = [];
    const labelsNew: SummaryEntity[] = [];
    allLabels.forEach((display, key) => {
      const inAll = tenantIds.every(tid => deployedLabelsPerTenant.get(tid)?.has(key) ?? false);
      (inAll ? labelsExisting : labelsNew).push({ key, display });
    });
    
    // Get rule summary per tenant
    const tenantRuleSummaries = tenantIds.map(tenantId => {
      const selectedRuleIds = deployWizard.tenantRuleSelections.get(tenantId) || new Set();
      const selectedRules = corpRules.filter(r => selectedRuleIds.has(r.ruleId));
      
      return {
        tenantId,
        tenantName: getTenantNameById(tenantId),
        prefix: getTenantPrefixById(tenantId),
        rules: selectedRules.map(r => ({ ruleId: r.ruleId, ruleName: r.ruleName })),
      };
    });
    
    const totalRulesCount = tenantRuleSummaries.reduce((sum, t) => sum + t.rules.length, 0);
    
    return {
      tenantRuleSummaries,
      totalRulesCount,
      sitsExisting,
      sitsNew,
      labelsExisting,
      labelsNew,
    };
  };

  return (
    <PolicyTableContainer
      title="Policies"
      subtitle="Manage policies for this tenant"
      selectedTab={selectedTab}
      onTabChange={(tab) => {
        setSelectedTab(tab);
        resetFilters();
        setExpanded(new Set());
        setPageNumber(1);
      }}
      dlpCount={dlpCount}
      autoLabelCount={autoLabelCount}
      error={error}
    >
      {/* Prefix redeploy warnings — non-blocking, dismissible (shared across views) */}
      <DeploymentWarningBanner />

      {/* Deployment result message bar */}
      {deployMessage && (
        <MessageBar
          intent={deployMessage.intent}
          style={{ marginBottom: 12 }}
        >
          <MessageBarBody>
            <MessageBarTitle>{deployMessage.title}</MessageBarTitle>
            {deployMessage.body}
          </MessageBarBody>
          <Button
            appearance="subtle"
            size="small"
            icon={<Dismiss24Regular />}
            onClick={() => setDeployMessage(null)}
            aria-label="dismiss message"
          />
        </MessageBar>
      )}

      {/* Active deployment polling indicator */}
      {activeDeployCount > 0 && (
        <MessageBar
          intent="info"
          style={{ marginBottom: 12 }}
        >
          <MessageBarBody>
            <MessageBarTitle>Deployment In Progress</MessageBarTitle>
            {activeDeployCount} deployment{activeDeployCount > 1 ? 's' : ''} in progress. Status updates automatically every 15 seconds.
          </MessageBarBody>
        </MessageBar>
      )}

      {/* Search bar */}
      <EntityFilterBar
        search={search}
        onSearchChange={setSearch}
        showFilter={false}
        searchAriaLabel="Search policies"
        searchPlaceholder="Search policies..."
        className={styles.solidField}
      />

      {/* Table */}
      <PolicyTable
        columns={[
          { key: 'expand', label: '', width: '48px' },
          { key: 'name', label: 'Policy Name', width: 'minmax(250px,2.5fr)' },
          ...(selectedTab === 'DLP' ? [{ key: 'priority', label: 'Priority', width: 'minmax(100px,0.8fr)' }] : []),
          { key: 'coverage', label: 'Tenant Coverage', width: 'minmax(180px,1.5fr)' },
          { key: 'lastModified', label: 'Last Modified', width: 'minmax(180px,1.5fr)', isTimeColumn: true },
          { key: 'actions', label: 'Actions', width: 'minmax(180px,1.5fr)', align: 'center' },
        ]}
        gridTemplateColumns={selectedTab === 'DLP' ? DASHBOARD_GRID_COLUMNS : DASHBOARD_GRID_COLUMNS_NO_PRIORITY}
        loading={loading}
        loadingMessage="Loading policies..."
        isEmpty={filteredPolicies.length === 0}
        selectedTab={selectedTab}
        emptyMessage={`No ${selectedTab === 'DLP' ? 'DLP' : 'Auto Label'} policies match your filters`}
      >
        {filteredPolicies.map((policy, i) => {
          const isExpanded = expanded.has(policy.policyId);
          const isLastRow = i === filteredPolicies.length - 1;

          return (
            <div key={policy.policyId}>
              <DashboardPolicyRow
                policy={policy}
                gridColumns={selectedTab === 'DLP' ? DASHBOARD_GRID_COLUMNS : DASHBOARD_GRID_COLUMNS_NO_PRIORITY}
                showPriority={selectedTab === 'DLP'}
                isExpanded={isExpanded}
                isLastRow={isLastRow}
                getTenantNameById={getTenantNameById}
                onToggleExpand={() => handleToggleExpand(policy.policyId)}
                onViewDetails={() => handleViewDetails(policy)}
                onStartDeployment={() => handleStartDeployment(policy)}
              />

              {/* Expanded tenant details */}
              {isExpanded && (
                <TenantExpandedRow
                  tenantDeployments={policy.tenantDeployments}
                  getTenantNameById={getTenantNameById}
                  getTenantPrefixById={getTenantPrefixById}
                  isLastRow={isLastRow}
                />
              )}
            </div>
          );
        })}
      </PolicyTable>

      {/* Pagination footer */}
      <PoliciesPaginationFooter
        pageNumber={metadata.pageNumber}
        totalPages={metadata.totalPages}
        pageSize={metadata.pageSize}
        hasNextPage={metadata.hasNextPage}
        loading={loading}
        onPageChange={setPageNumber}
      />

      {/* View Details Dialog */}
      <Dialog open={viewDetailsDialog.isOpen} onOpenChange={(_, data) => data.open ? null : viewDetailsDialog.close()}>
        <DialogSurface style={DIALOG_STYLES.surface}>
          <DialogBody>
            <DialogTitle
              action={
                <Button
                  appearance="subtle"
                  aria-label="Close dialog"
                  title="Close dialog"
                  icon={<Dismiss24Regular />}
                  onClick={viewDetailsDialog.close}
                />
              }
            >
              {viewDetailsDialog.data?.policyName}
            </DialogTitle>
            <DialogContent style={DIALOG_STYLES.content}>
              {viewDetailsDialog.data && <DashboardViewDetailsContent policy={viewDetailsDialog.data} />}
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={viewDetailsDialog.close}>
                Close
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Tenant Selection Dialog - Step 1 */}
      {deployWizard.step === 'tenant-selection' && (
        <TenantSelectionStep
          policy={deployWizard.policy}
          localSelectedTenants={localSelectedTenants}
          error={deployWizard.error}
          getTenantNameById={getTenantNameById}
          getTenantPrefixById={getTenantPrefixById}
          onTenantToggle={handleTenantToggle}
          onNext={handleNextToRuleSelection}
          onClose={deployWizardActions.closeWizard}
        />
      )}

      {/* Rule Selection Dialog - Step 2 */}
      {deployWizard.step === 'rule-selection' && deployWizard.policy && (
        <RuleSelectionStep
          policy={deployWizard.policy}
          wizardState={deployWizard}
          getTenantNameById={getTenantNameById}
          getTenantPrefixById={getTenantPrefixById}
          getActiveRuleIds={getActiveRuleIds}
          onRuleToggle={handleRuleToggle}
          onSetActiveTenant={deployWizardActions.setActiveTenant}
          onToggleRuleExpand={deployWizardActions.toggleRuleExpand}
          onBack={deployWizardActions.goBackToTenantSelection}
          onNext={handleNextToSummary}
          onClose={deployWizardActions.closeWizard}
        />
      )}

      {/* Summary Dialog - Step 3 */}
      {deployWizard.step === 'summary' && deployWizard.policy && (
        <SummaryStep
          policy={deployWizard.policy}
          summary={getDeploySummary()}
          isRefreshing={summaryRefresh.isRefreshing}
          refreshError={summaryRefresh.error}
          onRetryRefresh={summaryRefresh.retry}
          onBack={handleBackToRuleSelection}
          onConfirm={handleConfirmDeploy}
          onClose={deployWizardActions.closeWizard}
        />
      )}
    </PolicyTableContainer>
  );
}
